import { useState, useRef } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Gift, RotateCcw, Star } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// ===================== DATA =====================

const SPIN1 = [
  { label: "KES 10,000", value: 10000 },
  { label: "KES 5,000", value: 5000 },
  { label: "KES 1,000", value: 1000 },
  { label: "KES 500", value: 500 },
  { label: "KES 150", value: 150 },
  { label: "KES 100", value: 100 },
  { label: "KES 50", value: 50 },
  { label: "KES 20", value: 20 },
  { label: "KES 10", value: 10 },
  { label: "Spin Again", value: 0 },
];

const SPIN2 = [
  { label: "75” SONY TV", type: "big" },
  { label: "55” LG TV", type: "big" },
  { label: "43” Samsung TV", type: "big" },
  { label: "Home Theater", type: "big" },
  { label: "KES 1000 Bonus", type: "cash" },
  { label: "KES 500 Bonus", type: "cash" },
  { label: "Spin Again", type: "retry" },
  { label: "Spin Again", type: "retry" },
];

// ===================== COMPONENT =====================

export default function SpinWinPage() {
  const [tab, setTab] = useState("spin1");
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState("");
  const [show, setShow] = useState(false);

  const [activated, setActivated] = useState(false);

  // 👉 replace with API balance
  const [balance, setBalance] = useState(500);

  const segments = tab === "spin1" ? SPIN1 : SPIN2;
  const SEGMENT_ANGLE = 360 / segments.length;

  const spinCost = tab === "spin1" ? 20 : 100;

  const spin = () => {
    if (!activated) {
      alert("Activate first!");
      return;
    }

    if (balance < spinCost) {
      alert("Insufficient balance");
      return;
    }

    setBalance((b) => b - spinCost);

    setSpinning(true);

    const extra = 5 + Math.random() * 5;
    const land = Math.random() * 360;
    const total = rotation + extra * 360 + land;

    setRotation(total);

    setTimeout(() => {
      const angle = (360 - (total % 360)) % 360;
      const index = Math.floor(angle / SEGMENT_ANGLE);
      const won = segments[index];

      setResult(won.label);
      setShow(true);
      setSpinning(false);

      // 🎉 CONFETTI
      if (won.label !== "Spin Again") {
        confetti();
      }

      // 💰 SPIN 1 WIN
      if (tab === "spin1" && "value" in won && won.value > 0) {
        setBalance((b) => b + won.value);
      }

      // 🔁 Spin again
      if (won.label === "Spin Again") {
        alert("You got another spin!");
      }
    }, 4000);
  };

  const size = 300;
  const center = size / 2;
  const radius = center - 5;

  const path = (i: number) => {
    const start = (i * SEGMENT_ANGLE * Math.PI) / 180;
    const end = ((i + 1) * SEGMENT_ANGLE * Math.PI) / 180;

    const x1 = center + radius * Math.cos(start);
    const y1 = center + radius * Math.sin(start);
    const x2 = center + radius * Math.cos(end);
    const y2 = center + radius * Math.sin(end);

    return `M${center},${center} L${x1},${y1} A${radius},${radius} 0 0 1 ${x2},${y2} Z`;
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      {/* HEADER */}
      <div className="flex items-center gap-3">
        <Gift className="text-primary" />
        <h1 className="text-2xl font-bold">Spin & Win</h1>
      </div>

      {/* BALANCE */}
      <Card>
        <CardContent className="flex justify-between p-4">
          <span>Your Balance</span>
          <Badge>KES {balance}</Badge>
        </CardContent>
      </Card>

      {/* TABS */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="spin1">Cash Spin</TabsTrigger>
          <TabsTrigger value="spin2">Mega Spin</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          <Card className="p-6 flex flex-col items-center gap-6">

            {/* ACTIVATE */}
            {!activated && (
              <Button onClick={() => setActivated(true)}>
                Activate Free Spin
              </Button>
            )}

            {/* WHEEL */}
            <div className="relative">
              {/* POINTER */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <div className="w-0 h-0 border-l-[12px] border-r-[12px] border-t-[20px] border-transparent border-t-primary" />
              </div>

              <motion.div
                animate={{ rotate: rotation }}
                transition={{ duration: 4 }}
              >
                <svg width={size} height={size}>
                  {segments.map((s, i) => (
                    <g key={i}>
                      <path
                        d={path(i)}
                        fill={`hsl(${i * 40},70%,50%)`}
                        stroke="#fff"
                      />
                      <text
                        x={center}
                        y={center - radius + 30}
                        textAnchor="middle"
                        transform={`rotate(${i * SEGMENT_ANGLE + SEGMENT_ANGLE / 2}, ${center}, ${center})`}
                        className="fill-white text-[10px]"
                      >
                        {s.label}
                      </text>
                    </g>
                  ))}
                </svg>
              </motion.div>
            </div>

            {/* BUTTON */}
            <Button
              onClick={spin}
              disabled={spinning}
              className="px-10 py-6 text-lg rounded-full"
            >
              {spinning ? "Spinning..." : `Spin (KES ${spinCost})`}
            </Button>

            {/* INFO */}
            <p className="text-sm text-muted-foreground">
              {tab === "spin1"
                ? "Win instant cash rewards"
                : "Win TVs & big prizes"}
            </p>
          </Card>
        </TabsContent>
      </Tabs>

      {/* RESULT */}
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="text-center">
          <h2 className="text-xl font-bold">
            {result === "Spin Again"
              ? "Try Again 😅"
              : "Congratulations 🎉"}
          </h2>
          <p className="text-lg mt-2">{result}</p>

          <Button className="mt-4" onClick={() => setShow(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}